import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { RoleType } from "../../packages/domain/iam/iam.types";
import { UserProfile } from "@dirootie-domain/iam";
import { useAccount, useIsAuthenticated } from "@azure/msal-react";
import { SessionContext } from "./session.context";
import { AzureAdToken } from "../../packages/domain/iam/azure-ad.types";
import {
  AuthenticationResult,
  PublicClientApplication,
} from "@azure/msal-browser";
import { IKeyedObject } from "@dirootie-extensions/typescript";

function getHighestRole(roles: string[]): RoleType {
  if (roles.includes('Access.Admin')) return 'STUDY_DIRECTOR';
  if (roles.includes('Access.Lecturer')) return 'LECTURER';
  if (roles.includes('Access.Student')) return 'STUDENT';

  throw new Error('No supported role found');
}

/**
 * Props for the SessionProvider component
 *
 * @typedef {Object} SessionProviderProps
 * @property {ReactNode} children - The children elements to be rendered inside the provider.
 */
type SessionProviderProps = {
  children: ReactNode;
};

/**
 * Role Mapper
 *
 * Maps roles from the token to the application roles.
 *
 * @type {IKeyedObject<RoleType>}
 */
export const roleMapper: IKeyedObject<RoleType> = {
  Admin: "STUDY_DIRECTOR",
  Lecturer: "LECTURER",
  Student: "STUDENT",
  "Access.Admin": "STUDY_DIRECTOR",
  "Access.Lecturer": "LECTURER",
  "Access.Student": "STUDENT",
};

/**
 * Role Mapper Text
 *
 * This object provides human-readable role names based on the application's internal roles.
 * It is used for displaying role names in the UI (e.g., "Study Director", "Lecturer").
 *
 * @type {IKeyedObject<string>}
 */
export const roleMapperText: IKeyedObject<string> = {
  STUDY_DIRECTOR: "Study Director",
  LECTURER: "Lecturer",
  STUDENT: "Student",
};

/**
 * Extracts user profile information from the Azure AD token.
 *
 * @param {AzureAdToken} token - The token containing user information.
 * @returns {UserProfile} - The extracted user profile.
 */
const getUserFromToken = (token: AzureAdToken) => {
  const {
    idTokenClaims: { oid: id, roles },
    name,
    username,
    localAccountId,
  } = token;

  if (!roles)
    throw new Error(
      'The accessToken generated by AzureB2C must contain an attribute "roles" with an array that must contain the roles for the current logged in user.'
    );

  if (roles.length === 0)
    throw new Error(
      "The roles property in the accesssToken generated by AzureB2C must contain at least one role for the current logged in user."
    );

  const role = getHighestRole(roles);
  const roleImpersonated = role;
  const profile = {
    id,
    name,
    username,
    role,
    roleImpersonated,
    email: username,
    localAccountId,
  };
  return profile as any;
};

/**
 * Calculates the role impersonated based on the current role and URL path.
 *
 * @param {RoleType} role - The current role of the user.
 * @returns {RoleType} - The calculated role impersonated.
 */
const calculateRoleImpersonated = (role: RoleType) => {
  const location = window.location.pathname;
  if (role === "STUDENT" || location.startsWith("/students")) {
    return "STUDENT";
  }
  if (location.startsWith("/lecturers")) {
    return "LECTURER";
  }
  if (role === "STUDY_DIRECTOR") {
    return "STUDY_DIRECTOR";
  }
  return "LECTURER";
};

/**
 * SessionProvider Component
 *
 * Provides session context to the children components, managing user profile and role information.
 *
 * @param {SessionProviderProps} props - The props for the component.
 * @returns {JSX.Element} - The rendered session provider.
 */
export const SessionProvider: React.FC<SessionProviderProps> = ({
  children,
}: any) => {
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount();
  const [userProfile, setUserProfile] = useState<UserProfile | undefined>(
    undefined
  );
  const [roleImpersonated, setImpersonatedRole] = useState<
    RoleType | undefined
  >(undefined);

  /**
   * Handles successful login by setting the user profile and impersonated role.
   *
   * @deprecated
   * @param {AzureAdToken} account - The token containing account information.
   */
  const handleLoginSuccess = (account: AzureAdToken) => {
    const userProfile = getUserFromToken(account);
    const { role, roleImpersonated, ...rest } = userProfile;
    const userRole = role;
    setUserProfile({ role: userRole, ...rest });
    setImpersonatedRole(calculateRoleImpersonated(userRole));
  };

  useEffect(() => {
    if (account && isAuthenticated) {
      const userProfile = getUserFromToken(account as AzureAdToken);
      const { role, roleImpersonated, ...rest } = userProfile;
      const userRole = role;
      setUserProfile({
        role: userRole,
        ...rest,
      });
      setImpersonatedRole(calculateRoleImpersonated(userRole));
    } else {
      setUserProfile(undefined);
      setImpersonatedRole(undefined); // this is where the default role is set, something is wrong because right now, the account is null even if i am authenticated
    }
  }, [account, isAuthenticated]);

  /**
   * Gets the route based on the user's role.
   *
   * @returns {string} - The route path based on the role.
   */
  const getRouteBasedOnRole = () => {
    switch (userProfile?.role) {
      case "STUDY_DIRECTOR":
        return "/management";
      case "LECTURER":
        return "/lecturers";
      case "STUDENT":
        return "/students";
      default:
        return "/students";
    }
  };

  const value = useMemo(
    () => ({
      userProfile,
      roleImpersonated,
      isAuthenticated,
      setUserProfile,
      setImpersonatedRole,
      handleLoginSuccess,
      getRouteBasedOnRole,
    }),
    [userProfile, roleImpersonated, isAuthenticated]
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};
